import { Box, Button, Grid, IconButton, LinearProgress, Pagination, Paper, styled, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { USER_NAME_ROLE } from "src/app/auth";
import { deleteAllSystemStatusLogs, getSystemStatusLogs } from "src/app/services";
import { SystemStatusLog } from "src/app/types";
import { convertUTCDateToLocalTime, formatDateSafe, formatToUTC } from "src/app/utils/helper";
import { format } from "date-fns";
import { downloadFile } from "src/app/utils/functions";
import { Download } from "@mui/icons-material";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#e8e8ed",
        color: theme.palette.common.black,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const SystemLogsPageContent = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [logs, setLogs] = useState<SystemStatusLog[]>([]);
    const [page, setPage] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);

    const handleClearLogsClick = () => {
        deleteAllSystemStatusLogs().then(() => {
            enqueueSnackbar({
                message: "All system status logs cleared successfully.",
                variant: "success",
            });
            setLogs([]);
        }).catch((err) => {
            console.error("Error clearing system status logs: ", err);
            enqueueSnackbar({
                message: "Error clearing system status logs.",
                variant: "error",
            });
        });
    }

    useEffect(() => {
        setLoading(true);
        getSystemStatusLogs(page)
            .then((response) => {
                setLogs(response.data.data);
                setTotalLogs(response.data.total);
            })
            .catch((err) => {
                enqueueSnackbar({
                    message: "Error fetching system status logs.",
                    variant: "error",
                });
            })
            .finally(() => setLoading(false));
    }, [page]);

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ my: loading ? 1 : 2 }}>{loading && <LinearProgress />}</Box>
            {!loading && sessionStorage.getItem(USER_NAME_ROLE) === "admin" && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mb: 2,
                    }}
                >
                    <Button
                        size="medium"
                        sx={{ mr: "18px" }}
                        variant="outlined"
                        onClick={handleClearLogsClick}
                    >
                        Clear Logs
                    </Button>
                </Box>
            )}
            <Grid container>
                <Grid item xs={12} lg={12}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 500 }} aria-label="customized table">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell align="center">Logged At</StyledTableCell>
                                    <StyledTableCell align="center">
                                        User
                                    </StyledTableCell>
                                    <StyledTableCell align="center">Action Type</StyledTableCell>
                                    <StyledTableCell align="center">Status</StyledTableCell>
                                    {/* <StyledTableCell align="center">Backup Restored At</StyledTableCell> */}
                                    {/* <StyledTableCell align="center">
                                        VIB 360 Running Status
                                    </StyledTableCell> */}
                                    <StyledTableCell align="center">Download</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.length === 0 && (
                                    <StyledTableRow>
                                        <StyledTableCell align="center" colSpan={7}>
                                            {loading ? "Loading..." : "No logs."}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                                {logs.map((row, i) => (
                                    <StyledTableRow key={i}>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {formatToUTC(row.created_at)} {!/^\d{10}$/.test(String(row.created_at)) ? "UTC" : ""}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.created_by !== null ? (row.created_by === 1 ? "Admin" : "Client") : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.type ? row.type : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center" component="th" scope="row">
                                            {row.status ? row.status : "-"}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <IconButton
                                                onClick={() => {
                                                    const content = `
                                                        Logged At: ${formatToUTC(row.created_at)} ${!/^\d{10}$/.test(String(row.created_at)) ? "UTC" : ""}
                                                        User: ${row.created_by !== null ? (row.created_by === 1 ? "Admin" : "Client") : "-"}
                                                        Action Type: ${row.type ? row.type : "-"}
                                                        Status: ${row.status ? row.status : "-"}
                                                        `.replace(/^\s+/gm, "").trim();

                                                    downloadFile(
                                                        `system-status-log-${row.created_at}.txt`,
                                                        content,
                                                        "text/plain"
                                                    );
                                                }}
                                                aria-label="Export txt"
                                                color="primary"
                                                title="Download File"
                                            >
                                                <Download />
                                            </IconButton>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: "20px",
                        }}
                    >
                        <Pagination
                            count={Math.ceil(totalLogs / 10)}
                            shape="rounded"
                            size="large"
                            page={page}
                            onChange={(e, newPage) => setPage(newPage)}
                            color="primary"
                        />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SystemLogsPageContent;